// Global Instructions Rule Applied!
//
// A waterfall tier in words, as the agreement states it. One wording for the
// two places that print it: the fund Terms tab's Waterfall section ("As the
// agreement reads", from the tier being edited) and the distribution form's
// Tiers strip (from the tier frozen in the distribution's snapshot). The
// callers resolve their own ids / codes to labels and pass the values here,
// so the sentence cannot drift between the two. With the fund's default carry
// terms (fundDefaults) a commitment-sourced figure prints the default beside
// its name: "the commitment base carry rate (fund default 20%)".

import { effectiveFundCarryDefaults, type FundCarryDefaults } from '../../../../../core/utils/fundCarryDefaults.ts'

export const TIER_LP_CLASS = 'LP'
export const TIER_GP_CLASS = 'GP'
export const TIER_METRIC_MULTIPLE = 'Multiple'
export const TIER_METRIC_IRR = 'IRR'
export const TIER_METRIC_PROFIT_SHARE = 'Profit Share'
export const TIER_SOURCE_FIXED = 'fixed'
export const TIER_SOURCE_COMMITMENT_BASE_CARRY = 'commitment_base_carry'
export const TIER_SOURCE_COMMITMENT_STEPPED_CARRY = 'commitment_stepped_carry'
export const TIER_SOURCE_COMMITMENT_BASE_HURDLE = 'commitment_base_hurdle'
export const TIER_SOURCE_COMMITMENT_STEPPED_HURDLE = 'commitment_stepped_hurdle'

/** A rate source as the wording needs it: its code decides the form, its label is printed. */
export interface TierRateSource {
  code: string
  label: string
}

export interface TierTerms {
  /** the GP split's rate source; null = the tier has no GP split */
  gpSource: TierRateSource | null
  /** the GP's fixed share (a fraction) when its source is fixed */
  gpSplitPercent: number | null | undefined
  /** the tier has no stop: it absorbs everything left */
  isNone: boolean
  metricLabel: string | null | undefined
  baseLabel: string | null | undefined
  /** whose receipts the stop counts: the class label */
  stopClassLabel: string | null | undefined
  thresholdSource: TierRateSource | null | undefined
  stopThreshold: number | null | undefined
  stopTierGroup: string | null | undefined
  /** the stop base is "Distributions under tier group" */
  isTierGroupBase: boolean
  /**
   * A Cross-Pool Loss Offset tier (a waterfall with pools): the pools it
   * offsets against, by code - empty = every other pool. Absent on any other
   * tier.
   */
  lossOffsetPools?: string[] | null
  /**
   * The fund's default carry terms (funds.base_carry_rate ...): printed beside a
   * figure sourced from the commitment terms, since that is what every
   * commitment without a side-letter term runs at. Absent = the name alone.
   */
  fundDefaults?: FundCarryDefaults | null
}

const pct = (v: number) => `${Math.round(v * 10000) / 100}%`

/** The fund's default for a commitment-sourced figure, as "20%" or "3×"; null without one. */
export function fundDefaultFigure(source: TierRateSource | null | undefined, defaults: FundCarryDefaults | null | undefined): string | null {
  if (!source || !defaults) return null
  const d = effectiveFundCarryDefaults(defaults)
  switch (source.code) {
    case TIER_SOURCE_COMMITMENT_BASE_CARRY:
      return d.base_carry_rate == null ? null : pct(d.base_carry_rate)
    case TIER_SOURCE_COMMITMENT_STEPPED_CARRY:
      return d.stepped_carry_rate == null ? null : pct(d.stepped_carry_rate)
    case TIER_SOURCE_COMMITMENT_BASE_HURDLE:
      return d.base_hurdle_multiple == null ? null : `${d.base_hurdle_multiple}×`
    case TIER_SOURCE_COMMITMENT_STEPPED_HURDLE:
      return d.stepped_hurdle_multiple == null ? null : `${d.stepped_hurdle_multiple}×`
    default:
      return null
  }
}

/** "the commitment base carry rate", with " (fund default 20%)" when the fund states one. */
function sourcedFigure(source: TierRateSource | null | undefined, defaults: FundCarryDefaults | null | undefined): string {
  const name = `the ${(source?.label ?? '?').toLowerCase()}`
  const fig = fundDefaultFigure(source, defaults)
  return fig ? `${name} (fund default ${fig})` : name
}

/** The Cross-Pool Loss Offset tier: it stops when the other pools' realized losses are offset. */
export function describeLossOffset(pools: string[] | null | undefined): string {
  const one = pools?.length === 1
  const whose = pools && pools.length > 0 ? `${one ? 'pool' : 'pools'} ${pools.join(', ')}` : 'the other pools'
  return `100% to the LPs until the realized losses of ${whose} are offset - as return of ${one ? "that pool's" : "those pools'"} capital, without carry.`
}

export function describeTierTerms(t: TierTerms): string {
  if (t.lossOffsetPools !== undefined) return describeLossOffset(t.lossOffsetPools)
  const gpSource = t.gpSource
  let share: string
  if (!gpSource) share = '100% to the LPs'
  else if (gpSource.code === TIER_SOURCE_FIXED) {
    const gp = t.gpSplitPercent == null ? '?' : `${Math.round(t.gpSplitPercent * 10000) / 100}%`
    const lp = t.gpSplitPercent == null ? '?' : `${Math.round((1 - t.gpSplitPercent) * 10000) / 100}%`
    share = gp === '100%' ? '100% to the GP' : `${lp} to the LPs, ${gp} to the GP`
  } else share = `${sourcedFigure(gpSource, t.fundDefaults)} to the GP, the rest to the LPs`

  if (t.isNone) return `${share} thereafter.`

  const metric = t.metricLabel
  const base = t.baseLabel
  const stopOn = t.stopClassLabel
  const source = t.thresholdSource
  const who = stopOn === TIER_GP_CLASS ? 'the GP' : stopOn === TIER_LP_CLASS ? 'the LPs' : 'the recipient'
  const tierWord = `tier${t.stopTierGroup?.includes('-') ? 's' : ''} ${t.stopTierGroup ?? '?'}`
  const baseText = t.isTierGroupBase
    ? `distributions under ${tierWord}`
    : `${(base ?? '?').toLowerCase()}${t.stopTierGroup ? ` (counting ${tierWord})` : ''}`
  const figure = (suffix: string) =>
    source?.code === TIER_SOURCE_FIXED
      ? t.stopThreshold == null
        ? '?'
        : suffix === '%'
          ? `${Math.round(t.stopThreshold * 10000) / 100}%`
          : `${t.stopThreshold}${suffix}`
      : sourcedFigure(source, t.fundDefaults)
  if (metric === TIER_METRIC_MULTIPLE) return `${share} until ${who} have received ${figure('×')} of ${baseText}.`
  if (metric === TIER_METRIC_PROFIT_SHARE) return `${share} until ${who} hold ${figure('%')} of ${baseText}.`
  if (metric === TIER_METRIC_IRR) return `${share} until ${who} reach an IRR of ${figure('%')} on ${baseText}.`
  return `${share} until ${who} have received ${figure('')} (${metric ?? 'stop'} on ${baseText}).`
}
